import { useEffect, useState, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSalesData } from '../../hooks/useSalesData';
import { useProducts } from '../../context/ProductsContext';
import {
  clearReturnAttempt,
  createReturnFingerprint,
  getOrCreateReturnAttempt,
  processSaleReturnApi,
} from '../../lib/salesApi';


// Formatea valores como $59.500
const formatCOP = value =>
  value !== undefined && value !== null
    ? value.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 })
    : '$0';

// Formatea fecha ISO a DD/MM/YYYY hh:mm a. m./p. m.
const formatDate = (val) => {
 if (!val) return '';
  const dateObj = typeof val?.toDate === 'function' ? val.toDate() : new Date(val);
 if (isNaN(dateObj)) return String(val); 
  return dateObj.toLocaleString('es-CO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
};

// Costea una línea de venta (soporta recetas)
function computeLineCost(line, productsMap) {
  const qty = Number(line.quantity || 0);

  // Si la venta trae desglose de receta, sumamos insumos
  if (Array.isArray(line.recipe) && line.recipe.length) {
    let perUnitCost = 0;
    for (const comp of line.recipe) {
const insumoId = comp.insumoId ?? comp.productId ?? comp.id;
    const insumo = productsMap.get(insumoId);      const unitCost = Number(insumo?.costPrice || 0); // costo por unidad/g/ml según esté guardado el insumo
      const compQty  = Number(comp.qtyPerUnit ?? comp.qty ?? 0);
      perUnitCost += unitCost * compQty;
    }
    return perUnitCost * qty;
  }

  // Si NO es receta: usa costPrice de la línea o, si no viene, del catálogo
 const baseCostPerUnit = Number(
   line.costPrice ?? productsMap.get(line.productId ?? line.id)?.costPrice ?? 0
 );
  return baseCostPerUnit * qty;
}

const normalizeMethodLabel = (m) => {
  const k = (m || '').toLowerCase();
  if (k.includes('efect')) return 'Efectivo';
  if (k.includes('débito') || k.includes('debito') || k.includes('crédito') || k.includes('credito') || k.includes('tarjeta')) return 'Tarjeta';
  if (k.includes('nequi')) return 'Nequi';
  if (k.includes('daviplata')) return 'Daviplata';
  if (k.includes('bancolombia')) return 'Bancolombia';
  if (k.includes('bbva')) return 'BBVA';
  if (k.includes('davivienda')) return 'Davivienda';
  if (k.includes('rappi')) return 'Rappi';
  if (k.includes('pse')) return 'PSE';
  if (k.includes('qr')) return 'QR';
  return m || 'Otros';
};

const AUTHORIZED_ADMIN_UTILITY_EMAILS = new Set([
  'torresdeysi55@gmail.com',
  'andresplata35@gmail.com',
]);

const SalesReportView = ({ onBack, initialSubView = 'summary', currentUser }) => {
const normalizedRole = String(currentUser?.role || '').trim().toLowerCase();
const normalizedEmail = String(currentUser?.email || '').trim().toLowerCase();
const canViewUtility = normalizedRole === 'ti' || (
  ['administrador', 'admin'].includes(normalizedRole) &&
  AUTHORIZED_ADMIN_UTILITY_EMAILS.has(normalizedEmail)
);
const [currentSubView, setCurrentSubView] = useState(initialSubView); // 'summary' | 'detail'
// Clave de fecha local YYYY-MM-DD (no UTC) para filtrar
const toLocalDateKey = (val) => {
  if (!val) return "";
  const d = typeof val?.toDate === "function" ? val.toDate() : new Date(val);
  if (isNaN(d)) return "";
 const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
};
const [filters, setFilters] = useState({
  startDate: '',
  endDate: '',
  paymentMethod: '',
  seller: '',
  customerQuery: ''   // nuevo
});

const [dateDraft, setDateDraft] = useState(() => ({ startDate: filters.startDate, endDate: filters.endDate }));
const datesPending = dateDraft.startDate !== filters.startDate || dateDraft.endDate !== filters.endDate;
const datesValid = (!dateDraft.startDate && !dateDraft.endDate) || Boolean(dateDraft.startDate && dateDraft.endDate && dateDraft.startDate <= dateDraft.endDate);
const { salesData: allSales, loading: salesLoading, error: salesError, truncated: salesTruncated, cachePersistent, reload: reloadSales } = useSalesData({
  // Keep one complete cache; changing filters never downloads known sales again.
  allHistory: true,
  max: 500,
});

const { products: catalog } = useProducts(true);
// Filtro de producto solo para subvista "detail"
const [productFilter, setProductFilter] = useState('');

const sellerOptions = useMemo(() => {
  const set = new Set(allSales.map(s => s.seller).filter(Boolean));
  return ["", ...Array.from(set)];
}, [allSales]);





const productsMap = useMemo(() => {
  return new Map(catalog.map(p => [p.id, p]));
}, [catalog]);

// (moved below filteredSales)


  const [selectedSale, setSelectedSale] = useState(null);
  const [showDailySummaryModal, setShowDailySummaryModal] = useState(false);
  const [dailySummaryData, setDailySummaryData] = useState(null);

const [returnModal, setReturnModal] = useState({
 open: false,
 sale: null,       // doc de la venta (objeto en allSales)
 line: null,       // línea de producto { productId/name/quantity/price... }
 lineIndex: null,
 qty: 1,
 mode: 'restock',  // 'restock' | 'no_restock'
 refundMethod: 'Efectivo',
 reason: 'Devolución desde reporte',
 processing: false,
});


const paymentOptions = ['Efectivo','Tarjeta','Nequi','Bancolombia','BBVA','Daviplata','Davivienda','Rappi','PSE','QR','Otros'];

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startDate' || name === 'endDate') {
      setDateDraft(prev => ({ ...prev, [name]: value }));
      return;
    }
    setFilters(prev => ({ ...prev, [name]: value }));
  };

function calculateUtility(sale, productsMap) {
  let utility = 0;
  if (Array.isArray(sale.products)) {
    for (const p of sale.products) {
      const revenue = Number(p.price || 0) * Number(p.quantity || 0);
      const cost    = computeLineCost(p, productsMap);
      utility += (revenue - cost);
    }
  }
  return utility;
}

async function refreshSalesAfterReturn() {
  if (reloadSales) await reloadSales();
}

async function executeReturn({ sale, lines, restock, refundMethod, reason }) {
  if (!sale?.id || !Array.isArray(lines) || lines.length === 0) {
    toast.error('Selecciona al menos un producto para devolver.');
    return;
  }

  const request = {
    saleId: sale.id,
    lines,
    restock,
    refundMethod,
    reason: String(reason || '').trim() || 'Devolución desde reporte',
  };
  const fingerprint = createReturnFingerprint(request);
  const attempt = getOrCreateReturnAttempt(fingerprint);
  setReturnModal((prev) => ({ ...prev, processing: true }));

  try {
    const result = await processSaleReturnApi({
      ...request,
      idempotencyKey: attempt.key,
    });
    clearReturnAttempt(attempt.key);
    setSelectedSale((current) => current?.id === sale.id
      ? {
          ...current,
          products: result.products,
          subtotal: result.subtotal,
          tax: result.tax,
          total: result.total,
          originalTotal: result.originalTotal,
          returnedTotal: result.returnedTotal,
          status: result.status,
        }
      : current);
    setReturnModal((prev) => ({ ...prev, open: false, processing: false }));
    await refreshSalesAfterReturn();
    toast.success(
      `Devolución procesada. Reembolso: ${formatCOP(Number(result.refundAmount || 0))}.`,
      { autoClose: 2600, closeOnClick: true, pauseOnHover: false }
    );
  } catch (error) {
    const uncertain = error?.uncertain === true;
    toast[uncertain ? 'warning' : 'error'](
      error?.message || 'No se pudo procesar la devolución.',
      { autoClose: uncertain ? 6000 : 4000 }
    );
    setReturnModal((prev) => ({ ...prev, processing: false }));
  }
}

async function handleReturnTransaction({ saleId, lineIndex, qty, mode, refundMethod, reason }) {
  const sale = allSales.find((item) => item.id === saleId) || returnModal.sale;
  const quantity = Number(qty);
  const line = sale?.products?.[lineIndex];
  if (!sale || !line || !Number.isFinite(quantity) || quantity <= 0) {
    toast.error('La línea o la cantidad de devolución no es válida.');
    return;
  }
  if (quantity > Number(line.quantity || 0)) {
    toast.error('La cantidad a devolver excede la cantidad pendiente.');
    return;
  }

  await executeReturn({
    sale,
    lines: [{ lineIndex, quantity }],
    restock: mode === 'restock',
    refundMethod,
    reason,
  });
}

async function handleFullReturnTransaction(sale) {
  const lines = (sale?.products || [])
    .map((line, lineIndex) => ({ lineIndex, quantity: Number(line.quantity || 0) }))
    .filter((line) => line.quantity > 0);
  if (lines.length === 0) {
    toast.info('Esta venta no tiene unidades pendientes por devolver.');
    return;
  }

  await executeReturn({
    sale,
    lines,
    restock: returnModal.mode === 'restock',
    refundMethod: returnModal.refundMethod,
    reason: returnModal.reason,
  });
}


const filteredSales = useMemo(() => {
  return allSales.filter(sale => {
    if (filters.startDate && sale.dateKey < filters.startDate) return false;
    if (filters.endDate && sale.dateKey > filters.endDate) return false;

if (filters.paymentMethod) {
  const sel = normalizeMethodLabel(filters.paymentMethod);
  const m1  = normalizeMethodLabel(sale.paymentMethod);
  const m2  = normalizeMethodLabel(sale.secondPaymentMethod);
  if (!(m1 === sel || m2 === sel)) return false;
}

    if (filters.seller && sale.seller !== filters.seller) return false;

    if (filters.customerQuery) {
      const q = filters.customerQuery.trim().toLowerCase();
      const name = (sale.customerName || sale.customer || '').toString().toLowerCase();
      const cc   = (sale.customerCC || '').toString().toLowerCase();
      if (!name.includes(q) && !cc.includes(q)) return false;
    }

    return true;
  });
}, [allSales, filters]);

const totalSales = useMemo(
  () => filteredSales.reduce((s, sale) => s + Number(sale.total ?? 0), 0),
  [filteredSales]
);

const totalUtility = useMemo(
  () => canViewUtility
    ? filteredSales.reduce((s, sale) => s + calculateUtility(sale, productsMap), 0)
    : 0,
  [filteredSales, productsMap, canViewUtility]
);

// Paginación del resumen
const SUMMARY_PAGE_SIZE = 30;
const [summaryPage, setSummaryPage] = useState(1);
const totalSummaryPages = useMemo(
  () => Math.max(1, Math.ceil(filteredSales.length / SUMMARY_PAGE_SIZE)),
  [filteredSales.length]
);
const pagedSummaryRows = useMemo(() => {
  const start = (summaryPage - 1) * SUMMARY_PAGE_SIZE;
  return filteredSales.slice(start, start + SUMMARY_PAGE_SIZE);
}, [filteredSales, summaryPage]);

// ---- Detalle por producto (aplanado) ----
const detailRows = useMemo(() => {
  const rows = [];
  for (const sale of filteredSales) {
    const prods = Array.isArray(sale.products) ? sale.products : [];
    for (let i = 0; i < prods.length; i++) {
      const p = prods[i] || {};
      const qty = Number(p.quantity || 0);
      const unitPrice = Number(p.price ?? p.priceSale ?? 0);
      const totalRevenue = unitPrice * qty;
      const totalCost = canViewUtility ? computeLineCost(p, productsMap) : 0;
      const costUnit = qty > 0 ? totalCost / qty : Number(p.costPrice || 0);
      rows.push({
        id: `${sale.id}_${p.id || p.name || i}`,
        date: sale.date,
        invoice: sale.invoiceNumber || '',
        product: p.name || '',
        costUnit,
        priceUnit: unitPrice,
        quantity: qty,
        utility: totalRevenue - totalCost,
        seller: sale.seller || ''
      });
    }
  }
  const term = (productFilter || '').trim().toLowerCase();
  return term ? rows.filter(r => (r.product || '').toLowerCase().includes(term)) : rows;
}, [filteredSales, productsMap, productFilter, canViewUtility]);

// Paginaci n del detalle
const DETAIL_PAGE_SIZE = 50;
const [detailPage, setDetailPage] = useState(1);
const totalDetailPages = useMemo(
  () => Math.max(1, Math.ceil(detailRows.length / DETAIL_PAGE_SIZE)),
  [detailRows]
);
const pagedDetailRows = useMemo(() => {
  const start = (detailPage - 1) * DETAIL_PAGE_SIZE;
  return detailRows.slice(start, start + DETAIL_PAGE_SIZE);
}, [detailRows, detailPage]);

// Reinicia p gina al cambiar filtros base o t rmino de producto
useEffect(() => { setDetailPage(1); }, [productFilter, filteredSales.length]);
useEffect(() => { setSummaryPage(1); }, [filters.startDate, filters.endDate, filters.paymentMethod, filters.seller, filters.customerQuery, filteredSales.length]);

  const generateDailySummary = () => {
const today = toLocalDateKey(new Date());
 if ((filters.startDate && today < filters.startDate) || (filters.endDate && today > filters.endDate)) {
   toast.info('Consulta un período que incluya hoy para generar el resumen diario.');
   return;
 }
 const dailySales = allSales.filter(sale => sale.dateKey === today);
const dailyTotal   = dailySales.reduce((sum, sale) => sum + Number(sale.total || 0), 0);
const dailyUtility = canViewUtility
  ? dailySales.reduce((sum, sale) => sum + calculateUtility(sale, productsMap), 0)
  : null;
    setDailySummaryData({
      date: today,
      totalSales: dailyTotal,
      totalUtility: dailyUtility,
      numberOfSales: dailySales.length
    });
    setShowDailySummaryModal(true);
  };

const generatePDF = () => {
  const rows = filteredSales.map(sale => {
    const productos = (sale.products || [])
      .map(p => `${Number(p.quantity || 0)} x ${p.name} (${formatCOP(Number(p.price || 0))})`)
      .join('<br/>');

    return `
      <tr>
        <td>${formatDate(sale.date) || ''}</td>
        <td>${sale.invoiceNumber || ''}</td>
        <td>${(sale.customerName || sale.customer || (sale.customerCC ? 'CC: ' + sale.customerCC : 'Consumidor Final'))}</td>
        <td>${productos}</td>
        <td class="num">${formatCOP(Number(sale.total || 0))}</td>
        ${canViewUtility ? `<td class="num">${formatCOP(calculateUtility(sale, productsMap))}</td>` : ''}
        <td>${normalizeMethodLabel(sale.paymentMethod)}${sale.secondPaymentMethod ? ' + ' + normalizeMethodLabel(sale.secondPaymentMethod) : ''}</td>
        <td>${sale.seller || ''}</td>
        <td>${sale.status || ''}</td>
      </tr>
    `;
  }).join('');

  const html = `
  <html>
    <head>
      <meta charset="utf-8" />
      <title>Reporte de Ventas</title>
      <style>
        @page { size: A4 landscape; margin: 12mm; }
        body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; color:#111; }
        h1 { font-size: 18px; margin: 0 0 10px; }
        .meta { font-size: 12px; color:#555; margin-bottom: 10px; }
        table { width:100%; border-collapse: collapse; font-size: 12px; table-layout: fixed; }
        th, td { border:1px solid #ddd; padding:6px 8px; vertical-align: top; }
        tr { page-break-inside: avoid; }
        th { background:#f5f5f5; text-align:left; }
        td.num { text-align: right; white-space: nowrap; }
        .small { font-size: 12px; color:#555; }
        .kpis { display:flex; gap:12px; margin: 10px 0 14px; }
        .kpi { padding:8px 10px; border:1px solid #eee; background:#fafafa; border-radius:8px; }
        .break { word-break: break-word; }
        .nowrap { white-space: nowrap; }
        .w-client { width: 18%; }
        .w-prods { width: 26%; }
        .w-seller { width: 16%; }
      </style>
    </head>
    <body>
      <h1>Reporte de Ventas</h1>
      <div class="meta">
        Generado: ${new Date().toLocaleString('es-CO')} ·
        Filtro: ${filters.startDate ? `${filters.startDate} a ${filters.endDate}` : 'Todo el historial'}
      </div>

      <div class="kpis">
        <div class="kpi">Total Ventas: <strong>${formatCOP(totalSales)}</strong></div>
        ${canViewUtility ? `<div class="kpi">Utilidad Estimada: <strong>${formatCOP(totalUtility)}</strong></div>` : ''}
        <div class="kpi">N° Transacciones: <strong>${filteredSales.length}</strong></div>
      </div>

      <table>
        <thead>
          <tr>
            <th class="nowrap">Fecha</th>
            <th class="nowrap">Factura</th>
            <th class="w-client">Cliente</th>
            <th class="w-prods">Productos</th>
            <th class="nowrap">Total</th>
            ${canViewUtility ? '<th class="nowrap">Utilidad</th>' : ''}
            <th class="nowrap">Método</th>
            <th class="w-seller">Vendedor</th>
            <th class="nowrap">Estado</th>
          </tr>
        </thead>
        <tbody>${rows || `<tr><td colspan="${canViewUtility ? 9 : 8}" class="small">Sin resultados para los filtros seleccionados.</td></tr>`}</tbody>
      </table>

      <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 300); };</script>
    </body>
  </html>`;

  const w = window.open('', '_blank');
  if (!w) { alert('El navegador bloqueó la ventana de impresión.'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
};

  
  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-400"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menú
        </button>
        <h1 className="text-4xl font-extrabold text-gray-900 drop-shadow-sm">Reporte de Ventas</h1>
        <div className="flex space-x-4">
          <button
            onClick={generateDailySummary}
            disabled={salesLoading || salesTruncated || Boolean(salesError) || datesPending}
            title={salesLoading ? 'Espera a que termine de cargar el periodo completo' : (salesTruncated ? 'Selecciona un rango de fechas para generar un resumen completo' : '')}
            className={`px-6 py-2 bg-indigo-600 text-white rounded-lg transition duration-200 shadow-md flex items-center ${(salesLoading || salesTruncated || Boolean(salesError) || datesPending) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700 hover:shadow-lg'}`}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Resumen Diario
          </button>
          <button
            onClick={generatePDF}
            disabled={salesLoading || salesTruncated || Boolean(salesError) || datesPending}
            title={salesLoading ? 'Espera a que termine de cargar el periodo completo' : (salesTruncated ? 'Selecciona un rango de fechas para exportar un reporte completo' : '')}
            className={`px-6 py-2 bg-red-600 text-white rounded-lg transition duration-200 shadow-md flex items-center ${(salesLoading || salesTruncated || Boolean(salesError) || datesPending) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700 hover:shadow-lg'}`}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2m7 0V5a2 2 0 00-2-2H7a2 2 0 00-2 2v6m7 0h2" />
            </svg>
            Exportar PDF
          </button>
        </div>
<ToastContainer
  position="top-center"
  theme="colored"
  newestOnTop
  limit={3}
  closeOnClick
  pauseOnFocusLoss={false}
/>


      </div>

      {salesLoading && (
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-blue-800">
          Sincronizando todo el historial… La primera carga obtiene todas las ventas; las siguientes consultan las versiones del registro y solo descargan los cambios pendientes.
        </div>
      )}
      {salesError && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span>No se pudo completar la carga del reporte. {salesError.message} No uses estos totales hasta volver a intentar.</span>
          <button
            onClick={reloadSales}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      )}
      {!salesLoading && !salesError && salesTruncated && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
          Vista previa: se muestran las {allSales.length} ventas más recientes. Selecciona una fecha de inicio y/o fin para que ValenPOS cargue el periodo completo antes de calcular o exportar.
        </div>
      )}
      {!salesLoading && !salesError && !salesTruncated && (
        <div className="mb-6 text-sm text-gray-600">
          <strong>{filters.startDate ? `Período consultado: ${filters.startDate} a ${filters.endDate}` : 'Todo el historial'}</strong>. Historial completo: <strong>{allSales.length}</strong> ventas. Con los filtros actuales: <strong>{filteredSales.length}</strong>.
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Filtros de Búsqueda</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
            <input
              type="date"
              name="startDate"
              value={dateDraft.startDate}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
            <input
              type="date"
              name="endDate"
              value={dateDraft.endDate}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Método Pago</label>
            <select
              name="paymentMethod"
              value={filters.paymentMethod}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            >
              <option value="">Todos</option>
              {paymentOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendedor</label>
<select
  name="seller"
  value={filters.seller}
  onChange={handleFilterChange}
  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
>
  {sellerOptions.map((opt, i) => (
    <option key={i} value={opt}>
      {opt === "" ? "Todos" : opt}
    </option>
  ))}
</select>
          </div>
          <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
  <input
    type="text"
    name="customerQuery"
    value={filters.customerQuery}
    onChange={handleFilterChange}
    placeholder="Nombre o cédula"
    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
  />
</div>

        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <button
          onClick={() => setFilters(prev => ({ ...prev, ...dateDraft }))}
          disabled={!datesValid || salesLoading || !datesPending}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-50"
        >Consultar</button>
        <button onClick={() => {
          setDateDraft({ startDate: '', endDate: '' });
          setFilters({ startDate: '', endDate: '', paymentMethod: '', seller: '', customerQuery: '' });
        }} disabled={salesLoading}
          className="px-4 py-2 rounded-lg border border-blue-600 text-blue-600 disabled:opacity-50">Todo el historial</button>
        <button onClick={reloadSales} disabled={salesLoading || datesPending}
          className="px-4 py-2 rounded-lg border border-blue-600 text-blue-600 disabled:opacity-50">Actualizar</button>
        <span className="text-sm text-gray-600">El historial se conserva en este dispositivo. Cambiar filtros no consulta Firebase. Actualizar verifica versiones y descarga solo los cambios pendientes.</span>
        {datesPending && <span role="status" className="text-sm text-amber-700">Fechas pendientes de consultar. Los resultados corresponden al período indicado arriba.</span>}
        {!datesValid && <span role="alert" className="text-sm text-red-700">Selecciona ambas fechas; la fecha inicial debe ser menor o igual a la final.</span>}
      </div>

      {cachePersistent === false && <p role="alert" className="mb-4 text-sm text-amber-700">El navegador no pudo guardar el reporte de forma permanente. Se conserva durante esta sesión; al cerrar el navegador será necesario cargarlo nuevamente.</p>}

      {/* Toggle de sub-vistas */}
      <div className="mb-4 flex gap-2 justify-center">
        <button
          onClick={() => setCurrentSubView('summary')}
          className={`px-4 py-2 rounded ${currentSubView==='summary' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
        >
          Resumen de Ventas
        </button>
        <button
          onClick={() => setCurrentSubView('detail')}
          className={`px-4 py-2 rounded ${currentSubView==='detail' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
        >
          Detalle por Producto
        </button>
      </div>

      <div className={`grid grid-cols-1 ${canViewUtility ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6 mb-8`}>
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">Total Ventas</h3>
<p className="text-4xl font-extrabold text-blue-600">{formatCOP(totalSales)}</p>
        </div>
        {canViewUtility && <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">Utilidad Estimada</h3>
<p className="text-4xl font-extrabold text-green-600">{formatCOP(totalUtility)}</p>
        </div>}
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <h3 className="text-lg font-medium text-gray-600 mb-2">N° de Transacciones</h3>
          <p className="text-4xl font-extrabold text-gray-800">{filteredSales.length}</p>
        </div>
      </div>

      {currentSubView === 'summary' && (
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
<table className="min-w-[1380px] w-full divide-y divide-gray-200 table-fixed">
  {/* ⬇️ El <colgroup> va AQUÍ, como hijo directo de <table> */}
<colgroup>
  {['10%','7%','11%','24%','8%','8%','10%','9%','6%','7%'].map((w,i) => (
    <col key={i} style={{ width: w }} />
  ))}
</colgroup>

  <thead className="bg-gray-50 sticky top-0 z-10">
    <tr>
      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Factura</th>
      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Productos</th>
      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
     {canViewUtility && <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilidad</th>}
      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Método Pago</th>
     <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendedor</th>
      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
    </tr>
  </thead>

<tbody className="bg-white divide-y divide-gray-200">
  {pagedSummaryRows.length > 0 ? (
    pagedSummaryRows.map((sale, idx) => (
<tr key={`${sale.id}-${idx}`} className="hover:bg-gray-50 transition duration-150">
  {/* FECHA */}
 <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-500">
      {formatDate(sale.date)}
  </td>

  {/* FACTURA */}
  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
    {sale.invoiceNumber}
  </td>

  {/* CLIENTE (permite salto de línea) */}
  <td className="px-4 py-3 text-sm text-gray-500 whitespace-normal break-words leading-tight max-w-[220px]">
    {sale.customerName || sale.customer || (sale.customerCC ? `CC: ${sale.customerCC}` : 'Consumidor Final')}
  </td>

  {/* PRODUCTOS: resumen compacto para no deformar ventas grandes */}
  <td className="px-4 py-3 text-sm text-gray-600 align-middle">
    {(() => {
      const products = Array.isArray(sale.products) ? sale.products : [];
      const preview = products.slice(0, 3);
      const totalUnits = products.reduce((sum, product) => sum + Number(product.quantity || 0), 0);
      return (
        <div className="space-y-1.5">
          {preview.map((product, index) => (
            <div key={`${product.id || product.productId || product.name}-${index}`} className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold text-slate-700">
                {Number(product.quantity || 0)}×
              </span>
              <span className="truncate" title={product.name}>{product.name}</span>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2 pt-0.5 text-xs">
            <span className="font-semibold text-slate-500">
              {totalUnits} {totalUnits === 1 ? 'unidad' : 'unidades'} · {products.length} {products.length === 1 ? 'referencia' : 'referencias'}
            </span>
            {products.length > preview.length && (
              <button
                type="button"
                onClick={() => setSelectedSale(sale)}
                className="rounded-full bg-blue-50 px-2.5 py-1 font-semibold text-blue-700 transition hover:bg-blue-100"
              >
                +{products.length - preview.length} productos
              </button>
            )}
          </div>
        </div>
      );
    })()}
  </td>

  {/* TOTAL */}
  <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
    {formatCOP(sale.total)}
  </td>

  {/* UTILIDAD */}
  {canViewUtility && <td className="px-4 py-3 whitespace-nowrap text-sm text-green-700 font-semibold">
    {formatCOP(calculateUtility(sale, productsMap))}
  </td>}

  {/* MÉTODO PAGO (igual que tenías) */}
 <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-500">
      {sale.secondPaymentMethod ? (
      <>
        <div>
          {normalizeMethodLabel(sale.paymentMethod)}: {formatCOP(Number(sale.amountReceived ?? 0))}
        </div>
        <div>
          {normalizeMethodLabel(sale.secondPaymentMethod)}: {formatCOP(Number(sale.secondAmountReceived ?? 0))}
        </div>
        {Number(sale.change || 0) > 0 && (
          <div className="text-xs text-gray-400">Cambio: {formatCOP(sale.change)}</div>
        )}
      </>
    ) : (
      <>
        <div>
          {normalizeMethodLabel(sale.paymentMethod)}: {formatCOP(Number(sale.amountReceived ?? sale.total ?? 0))}
        </div>
        {Number(sale.change || 0) > 0 && (
          <div className="text-xs text-gray-400">Cambio: {formatCOP(sale.change)}</div>
        )}
      </>
    )}
  </td>

  {/* VENDEDOR (rompe en emails largos) */}
  <td className="px-4 py-3 text-sm text-gray-500 whitespace-normal break-all leading-tight max-w-[260px]">
    {sale.seller}
  </td>

  {/* ESTADO */}
  <td className="px-4 py-3 whitespace-nowrap text-sm">
    <span
      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
        sale.status === 'Completada'
          ? 'bg-green-100 text-green-800'
          : sale.status === 'Devuelta'
          ? 'bg-red-100 text-red-800'
          : 'bg-yellow-100 text-yellow-800'
      }`}
    >
      {sale.status}
    </span>
  </td>

  {/* ACCIONES */}
  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
    <button
      onClick={() => setSelectedSale(sale)}
      className="text-blue-600 hover:text-blue-800 transition duration-150 mr-3"
    >
      Ver Detalles
    </button>
{sale.status !== 'Devuelta' && (
  <button
  onClick={() => setReturnModal({
    open: true,
    sale,
    line: null,
    lineIndex: null,
    qty: 1,
    mode: 'restock',
    refundMethod: normalizeMethodLabel(sale.paymentMethod),
    reason: 'Devolución desde reporte',
    processing: false,
  })}
    className="text-red-600 hover:text-red-800 transition duration-150"
  >
    Devolución
  </button>
)}
  </td>
</tr>
    ))
  ) : (
    <tr>
      <td colSpan="10" className="px-6 py-4 text-center text-sm text-gray-500">
        No se encontraron ventas con los filtros seleccionados.
      </td>
    </tr>
  )}
</tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-4 text-sm text-gray-600">
          <div>
            Mostrando {pagedSummaryRows.length ? (summaryPage - 1) * SUMMARY_PAGE_SIZE + 1 : 0}-{Math.min(summaryPage * SUMMARY_PAGE_SIZE, filteredSales.length)} de {filteredSales.length}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSummaryPage(1)}
              disabled={summaryPage === 1}
              className="px-3 py-1 rounded border disabled:opacity-50"
            >Primero</button>
            <button
              onClick={() => setSummaryPage(p => Math.max(1, p - 1))}
              disabled={summaryPage === 1}
              className="px-3 py-1 rounded border disabled:opacity-50"
            >Anterior</button>
            <button
              onClick={() => setSummaryPage(p => Math.min(totalSummaryPages, p + 1))}
              disabled={summaryPage === totalSummaryPages}
              className="px-3 py-1 rounded border disabled:opacity-50"
            >Siguiente</button>
            <button
              onClick={() => setSummaryPage(totalSummaryPages)}
              disabled={summaryPage === totalSummaryPages}
              className="px-3 py-1 rounded border disabled:opacity-50"
            >Último</button>
          </div>
        </div>
      </div>
      )}
      {currentSubView === 'detail' && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 flex flex-wrap gap-3 items-center">
            <input
              type="text"
              placeholder="Filtrar por producto..."
              value={productFilter}
              onChange={(e) => { setProductFilter(e.target.value); setDetailPage(1); }}
              className="px-4 py-2 border border-gray-300 rounded w-72"
            />
            <button
              type="button"
              onClick={() => {
                try {
                  const headers = canViewUtility
                    ? ['Fecha','Factura','Producto','Costo Unitario','Precio Venta','Cantidad','Utilidad','Vendedor']
                    : ['Fecha','Factura','Producto','Precio Venta','Cantidad','Vendedor'];
                  const rows = detailRows.map(r => canViewUtility ? [
                    formatDate(r.date) || '', r.invoice || '', (r.product || '').replace(/"/g,'""'),
                    Math.round(r.costUnit || 0), Math.round(r.priceUnit || 0), r.quantity || 0,
                    Math.round(r.utility || 0), (r.seller || '')
                  ] : [
                    formatDate(r.date) || '', r.invoice || '', (r.product || '').replace(/"/g,'""'),
                    Math.round(r.priceUnit || 0), r.quantity || 0, (r.seller || '')
                  ]);
                  const csv = [headers, ...rows]
                    .map(cols => cols.map(v => typeof v === 'string' ? `"${v}"` : v).join(','))
                    .join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `detalle_ventas_${Date.now()}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (e) { console.error('Export CSV error', e); }
              }}
              className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Exportar CSV
            </button>
            <div className="text-sm text-gray-600 ml-auto">
              Mostrando página {detailPage} de {totalDetailPages} ({detailRows.length} ítems)
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Factura</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                  {canViewUtility && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Costo Unitario</th>}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio Venta</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                  {canViewUtility && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilidad</th>}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendedor</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pagedDetailRows.length ? (
                  pagedDetailRows.map((r, idx) => (
                    <tr key={`${r.id}-${idx}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(r.date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{r.invoice}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{r.product}</td>
                      {canViewUtility && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCOP(r.costUnit)}</td>}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCOP(r.priceUnit)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{r.quantity}</td>
                      {canViewUtility && <td className="px-6 py-4 whitespace-nowrap text-sm text-green-700 font-semibold">{formatCOP(r.utility)}</td>}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{r.seller}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={canViewUtility ? 8 : 6} className="px-6 py-4 text-center text-sm text-gray-500">Sin datos para mostrar.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between p-4">
            <div className="text-sm text-gray-600">
              Mostrando {(detailRows.length ? (detailPage - 1) * DETAIL_PAGE_SIZE + 1 : 0)}–{Math.min(detailPage * DETAIL_PAGE_SIZE, detailRows.length)} de {detailRows.length}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setDetailPage(1)}
                disabled={detailPage === 1}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >Primero</button>
              <button
                onClick={() => setDetailPage(p => Math.max(1, p - 1))}
                disabled={detailPage === 1}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >Anterior</button>
              <button
                onClick={() => setDetailPage(p => Math.min(totalDetailPages, p + 1))}
                disabled={detailPage === totalDetailPages}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >Siguiente</button>
              <button
                onClick={() => setDetailPage(totalDetailPages)}
                disabled={detailPage === totalDetailPages}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >Último</button>
            </div>
          </div>
        </div>
      )}

      {selectedSale && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-sm sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sale-detail-title"
        >
          <div className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="shrink-0 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50 px-5 py-4 sm:px-7">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Detalle de venta</p>
                  <h2 id="sale-detail-title" className="mt-1 text-2xl font-black text-slate-900 sm:text-3xl">
                    {selectedSale.invoiceNumber || 'Sin número de factura'}
                  </h2>
                  <p className="mt-1 truncate text-sm text-slate-500">
                    {formatDate(selectedSale.date)} · {selectedSale.customerName || selectedSale.customer || 'Consumidor Final'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedSale(null)}
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-slate-200 bg-white text-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                  aria-label="Cerrar detalle"
                >
                  ×
                </button>
              </div>

              <div className={`mt-4 grid grid-cols-2 gap-2 ${canViewUtility ? 'sm:grid-cols-4' : 'sm:grid-cols-3'}`}>
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Productos</p>
                  <p className="mt-1 text-lg font-black text-slate-900">
                    {(selectedSale.products || []).reduce((sum, product) => sum + Number(product.quantity || 0), 0)} unidades
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Total</p>
                  <p className="mt-1 text-lg font-black text-blue-700">{formatCOP(selectedSale.total)}</p>
                </div>
                {canViewUtility && <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Utilidad estimada</p>
                  <p className="mt-1 text-lg font-black text-emerald-700">{formatCOP(calculateUtility(selectedSale, productsMap))}</p>
                </div>}
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Estado</p>
                  <p className="mt-1 truncate text-sm font-bold text-slate-800">{selectedSale.status || 'Sin estado'}</p>
                </div>
              </div>
            </div>

            <div className="grid shrink-0 grid-cols-1 gap-2 border-b border-slate-200 px-5 py-3 text-sm text-slate-600 sm:grid-cols-3 sm:px-7">
              <p className="truncate"><span className="font-bold text-slate-800">Cliente:</span> {selectedSale.customerName || selectedSale.customer || (selectedSale.customerCC ? `CC: ${selectedSale.customerCC}` : 'Consumidor Final')}</p>
              <p className="truncate" title={selectedSale.seller}><span className="font-bold text-slate-800">Vendedor:</span> {selectedSale.seller || 'N/A'}</p>
              <p className="truncate"><span className="font-bold text-slate-800">Pago:</span> {normalizeMethodLabel(selectedSale.paymentMethod)}{selectedSale.secondPaymentMethod ? ` + ${normalizeMethodLabel(selectedSale.secondPaymentMethod)}` : ''}</p>
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              <table className="min-w-[900px] w-full divide-y divide-slate-200">
                <thead className="sticky top-0 z-10 bg-slate-50 shadow-sm">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Producto</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Cant.</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Precio unit.</th>
                    {canViewUtility && <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Costo total</th>}
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Subtotal</th>
                    {canViewUtility && <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Utilidad</th>}
                    <th className="px-5 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {(selectedSale.products || []).map((product, idx) => {
                    const quantity = Number(product.quantity || 0);
                    const revenue = Number(product.price || 0) * quantity;
                    const lineCost = canViewUtility ? computeLineCost(product, productsMap) : 0;
                    const lineUtil = revenue - lineCost;
                    const recipeNames = Array.isArray(product.recipe)
                      ? product.recipe.map((component) => component.name).filter(Boolean)
                      : [];

                    return (
                      <tr key={`${product.id || product.productId || product.name}-${idx}`} className="hover:bg-blue-50/40">
                        <td className="px-5 py-3">
                          <p className="font-bold text-slate-900">{product.name}</p>
                          {recipeNames.length > 0 && (
                            <p className="mt-1 max-w-xl truncate text-xs text-slate-400" title={recipeNames.join(' · ')}>
                              {recipeNames.join(' · ')}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-700">{quantity}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{formatCOP(product.price)}</td>
                        {canViewUtility && <td className="px-4 py-3 text-right text-slate-600">{formatCOP(lineCost)}</td>}
                        <td className="px-4 py-3 text-right font-bold text-slate-900">{formatCOP(revenue)}</td>
                        {canViewUtility && <td className={`px-4 py-3 text-right font-bold ${lineUtil >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {formatCOP(lineUtil)}
                        </td>}
                        <td className="px-5 py-3 text-right">
                          {selectedSale.status !== 'Devuelta' && quantity > 0 ? (
                            <button
                              type="button"
                              onClick={() => {
                                const qtyToReturn = prompt(
                                  `¿Cuántas unidades de ${product.name} desea devolver? (Máximo: ${quantity})`
                                );
                                if (qtyToReturn && parseInt(qtyToReturn, 10) > 0) {
                                  setReturnModal({
                                    open: true,
                                    sale: selectedSale,
                                    line: product,
                                    lineIndex: idx,
                                    qty: Math.min(parseInt(qtyToReturn, 10), quantity),
                                    mode: 'restock',
                                    refundMethod: normalizeMethodLabel(selectedSale.paymentMethod),
                                    reason: 'Devolución desde reporte',
                                    processing: false,
                                  });
                                }
                              }}
                              className="whitespace-nowrap rounded-lg border border-red-200 px-3 py-1.5 text-xs font-bold text-red-600 transition hover:bg-red-50"
                            >
                              Devolver
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex shrink-0 flex-col gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
              <p className="text-xs text-slate-500">
                {(selectedSale.products || []).length} líneas en esta factura. Desplázate dentro de la tabla para revisarlas todas.
              </p>
              <button
                type="button"
                onClick={() => setSelectedSale(null)}
                className="rounded-xl bg-blue-600 px-6 py-2.5 font-bold text-white transition hover:bg-blue-700"
              >
                Cerrar detalle
              </button>
            </div>
          </div>
        </div>
      )}
      {showDailySummaryModal && dailySummaryData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Resumen Diario de Ventas</h2>
            <p className="text-lg mb-2"><strong>Fecha:</strong> {dailySummaryData.date}</p>
<p className="text-lg mb-2"><strong>Total de Ventas:</strong> <span className="font-bold text-blue-600">{formatCOP(dailySummaryData.totalSales)}</span></p>
{canViewUtility && <p className="text-lg mb-2"><strong>Utilidad Estimada:</strong> <span className="font-bold text-green-600">{formatCOP(dailySummaryData.totalUtility)}</span></p>}
            <p className="text-lg mb-4"><strong>Número de Ventas:</strong> <span className="font-bold text-gray-800">{dailySummaryData.numberOfSales}</span></p>
            <button
              onClick={() => setShowDailySummaryModal(false)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

{returnModal.open && returnModal.sale && (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-sm">
    <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
      <div className="border-b border-slate-200 bg-gradient-to-r from-red-50 to-white px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-600">Devolución de venta</p>
            <h3 className="mt-1 text-2xl font-black text-slate-900">
              Factura {returnModal.sale.invoiceNumber || returnModal.sale.id}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              El servidor recalcula el total y registra la devolución de forma segura.
            </p>
          </div>
          <button
            type="button"
            disabled={returnModal.processing}
            onClick={() => setReturnModal((prev) => ({ ...prev, open: false }))}
            className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 bg-white text-xl text-slate-500 disabled:opacity-50"
            aria-label="Cerrar devolución"
          >
            ×
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 border-b border-slate-200 bg-slate-50 px-6 py-4 md:grid-cols-3">
        <label className="text-xs font-bold uppercase tracking-wide text-slate-500">
          Manejo de inventario
          <select
            value={returnModal.mode}
            disabled={returnModal.processing}
            onChange={(event) => setReturnModal((prev) => ({ ...prev, mode: event.target.value }))}
            className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium normal-case text-slate-800"
          >
            <option value="restock">Reponer al inventario</option>
            <option value="no_restock">No reponer (consumido/dañado)</option>
          </select>
        </label>
        <label className="text-xs font-bold uppercase tracking-wide text-slate-500">
          Método del reembolso
          <select
            value={returnModal.refundMethod}
            disabled={returnModal.processing}
            onChange={(event) => setReturnModal((prev) => ({ ...prev, refundMethod: event.target.value }))}
            className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium normal-case text-slate-800"
          >
            {paymentOptions.map((method) => <option key={method} value={method}>{method}</option>)}
          </select>
        </label>
        <label className="text-xs font-bold uppercase tracking-wide text-slate-500">
          Motivo
          <input
            type="text"
            value={returnModal.reason}
            maxLength={240}
            disabled={returnModal.processing}
            onChange={(event) => setReturnModal((prev) => ({ ...prev, reason: event.target.value }))}
            className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium normal-case text-slate-800"
          />
        </label>
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-auto p-4 sm:p-6">
        {(returnModal.sale.products || []).map((product, idx) => {
          const available = Number(product.quantity || 0);
          const isSelected = returnModal.lineIndex === idx;
          return (
            <div
              key={`${product.productId || product.id || product.name}-${idx}`}
              className={`flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between ${isSelected ? 'border-blue-300 bg-blue-50' : 'border-slate-200'}`}
            >
              <div className="min-w-0">
                <p className="truncate font-bold text-slate-900">{product.name || product.productName}</p>
                <p className="text-sm text-slate-500">
                  Pendiente: {available} · Precio: {formatCOP(Number(product.price ?? product.priceSale ?? 0))}
                  {Number(product.returnedQuantity || 0) > 0 ? ` · Ya devuelto: ${Number(product.returnedQuantity)}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={available}
                  disabled={available <= 0 || returnModal.processing}
                  value={isSelected ? returnModal.qty : 1}
                  onChange={(event) => {
                    const value = Math.max(1, Math.min(Number(event.target.value || 1), available));
                    setReturnModal((prev) => ({ ...prev, line: product, lineIndex: idx, qty: value }));
                  }}
                  className="w-20 rounded-xl border border-slate-300 px-3 py-2 text-center disabled:bg-slate-100"
                  aria-label={`Cantidad a devolver de ${product.name || product.productName}`}
                />
                <button
                  type="button"
                  disabled={available <= 0 || returnModal.processing}
                  onClick={() => handleReturnTransaction({
                    saleId: returnModal.sale.id,
                    lineIndex: idx,
                    qty: isSelected ? returnModal.qty : 1,
                    mode: returnModal.mode,
                    refundMethod: returnModal.refundMethod,
                    reason: returnModal.reason,
                  })}
                  className="rounded-xl bg-blue-600 px-4 py-2 font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {returnModal.processing && isSelected ? 'Procesando…' : 'Devolver'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-between">
        <button
          type="button"
          disabled={returnModal.processing}
          onClick={() => setReturnModal((prev) => ({ ...prev, open: false }))}
          className="rounded-xl border border-slate-300 bg-white px-5 py-2.5 font-bold text-slate-700 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          disabled={returnModal.processing || !(returnModal.sale.products || []).some((line) => Number(line.quantity || 0) > 0)}
          onClick={() => {
            if (window.confirm('¿Confirmas devolver todas las unidades pendientes de esta venta?')) {
              handleFullReturnTransaction(returnModal.sale);
            }
          }}
          className="rounded-xl bg-red-600 px-5 py-2.5 font-bold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {returnModal.processing ? 'Procesando devolución…' : 'Devolver toda la venta'}
        </button>
      </div>
    </div>
  </div>
)}


    </div>
  );
};

export default SalesReportView;
